import type { LogFn } from '../../../log.js';
import type { AcpSessionAgentKey } from '../acp-agent.js';
import { AcpPromptRoutingRegistry } from '../acp-prompt-routing-registry.js';
import type { AcpClientState } from '../ensure-acp-client.js';

export type RunDispatchMeta = {
  acpSessionAgentKey: AcpSessionAgentKey;
};

/**
 * Mutable state shared across ACP manager helpers.
 * One ACP subprocess per cloud session + agent identity; prompt turns register session/run routing
 * while they stream so callbacks reach the correct turn.
 */
export type AcpManagerContext = {
  log: LogFn;
  /** Loopback bridge access server port for the shared MCP server subprocess. */
  getBridgeAccessPort: () => number;
  reportAgentCapabilities?: (info: { agentType: string; configOptions: unknown[] }) => void;
  /** From legacy `agent_config` WS message when present; prompts usually carry `agentType` per request. */
  backendFallbackAgentType: string | null;
  /** ACP client state per (cloud session id, agent spawn key). */
  acpAgents: Map<AcpSessionAgentKey, AcpClientState>;
  /** In-flight turn routing and active stream ownership per session+agent subprocess. */
  promptRouting: AcpPromptRoutingRegistry;
  /** Maps run id → session+agent subprocess for cancel / lookup. */
  runDispatch: Map<string, RunDispatchMeta>;
  /** When stop is requested while the agent process is still starting, skip sending once connected. */
  pendingCancelRunIds: Set<string>;
};

export function createAcpManagerContext(options: {
  log: LogFn;
  getBridgeAccessPort: () => number;
  reportAgentCapabilities?: (info: { agentType: string; configOptions: unknown[] }) => void;
}): AcpManagerContext {
  return {
    log: options.log,
    getBridgeAccessPort: options.getBridgeAccessPort,
    reportAgentCapabilities: options.reportAgentCapabilities,
    backendFallbackAgentType: null,
    acpAgents: new Map(),
    promptRouting: new AcpPromptRoutingRegistry(),
    runDispatch: new Map(),
    pendingCancelRunIds: new Set(),
  };
}
